import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import type { HomepageResults } from 'src/common/types/api/v4/search/v1/homepage';
import type { StickerCategories, StickerConfig } from 'src/common/components/Sticker/stickerMap';
import topDestinationSlugs from 'src/homepage/utils/topDestinationSlugs';
import Hero from 'src/homepage/components/Hero';
import DiscoverBoats from 'src/homepage/components/DiscoverBoats';
import LandingPageTopDestinations from 'src/common/components/LandingPageTopDestinations';
import HomepageExperiences from 'src/homepage/components/HomepageExperiences';
import HomepageMobileApps from 'src/homepage/components/HomepageMobileApps';
import HomepageCategories from 'src/homepage/components/HomepageCategories';
import ForOwners from 'src/homepage/components/ForOwners';
import StaticReviews from 'src/common/components/StaticReviews';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import HomepageInstabookListings from '../HomepageInstabookListings';

type HomePageContentProps = {
  homepage: HomepageResults;
  headerStickers?: Record<StickerCategories, StickerConfig> | null;
};

const HomePageContent: FC<HomePageContentProps> = ({ homepage, headerStickers }) => {
  const [t] = useTranslation('home-page');
  const { isMobileNativeWebView } = useMobileNativeWebView();

  const {
    expanded_search_radius: isExpandedSearch,
    recommended_listings: recommendedListings,
    instabook_listings: instabookListings,
    listing_types: listingTypes,
    experience_listings: experienceListings, // on dev, see `HomepageExperiences.stories.tsx`
  } = homepage;

  return (
    <>
      <Hero headerStickers={headerStickers} />
      {recommendedListings && (
        <DiscoverBoats
          isExpandedSearch={!!isExpandedSearch}
          listings={recommendedListings}
        />
      )}
      {instabookListings?.length && (
        <HomepageInstabookListings listings={instabookListings} />
      )}
      <LandingPageTopDestinations
        slugs={topDestinationSlugs}
        subtitle={!isMobileNativeWebView ? t('top-destinations--subtitle') : undefined}
        title={t('top-destinations--title')}
        translationFunction={t}
        layoutBackgroundColor={!isMobileNativeWebView ? 'pearl' : 'ivory'}
        isCompact={isMobileNativeWebView}
      />
      {experienceListings && experienceListings.length > 0 && (
        // on dev, this will never return
        // see `HomepageExperiences.stories.tsx`
        <HomepageExperiences
          experienceListings={experienceListings}
        />
      )}
      <HomepageMobileApps />
      {listingTypes && (
        <HomepageCategories
          listingTypes={listingTypes}
          layoutBackgroundColor={!isMobileNativeWebView ? 'midnight02' : 'ivory'}
          isCompact={isMobileNativeWebView}
        />
      )}
      {!isMobileNativeWebView && (
        <>
          <StaticReviews />
          <ForOwners />
        </>
      )}
    </>
  );
};

export default HomePageContent;
